import React from 'react';
import { Archive, Layers, Menu, Undo } from 'lucide-react';
import { MobileDrawer } from '../../mobile/MobileDrawer';
import { Loader } from '../../ui';

interface ArchivedWelcomeScreenProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
  onProjectSelect?: (projectId: string) => void;
  loading: boolean;
  user: Record<string, unknown>;
  navigate: (path: string) => void;
}

export function ArchivedWelcomeScreen({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  onProjectSelect,
  loading,
}: ArchivedWelcomeScreenProps) {
  return (
    <div className="h-full bg-gray-50">
      {/* Mobile Menu Button - Fixed position */}
      <button
        onClick={() => setIsMobileMenuOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-[40] p-2 bg-white rounded-lg shadow-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
      >
        <Menu size={24} />
      </button>

      {/* Mobile Drawer */}
      <MobileDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        projectId=""
        onProjectSelect={id => {
          onProjectSelect?.(id);
          setIsMobileMenuOpen(false);
        }}
      />

      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center max-w-md mx-auto">
          {/* Icon Group */}
          <div className="relative mb-8">
            {/* Main icon with glow effect */}
            <div className="relative w-24 h-24 mx-auto">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-500 to-gray-600 rounded-2xl opacity-75 blur-lg transform transition-all duration-200 group-hover:scale-110" />
              {/* Main icon */}
              <div className="relative w-24 h-24 bg-gradient-to-br from-gray-500 to-gray-600 rounded-2xl flex items-center justify-center shadow-[0_8px_16px_rgba(0,0,0,0.2)] transform transition-all duration-200 hover:scale-110 hover:shadow-[0_12px_24px_rgba(0,0,0,0.3)]">
                {loading ? <Loader /> : <Archive className="w-12 h-12 text-white" />}
              </div>
              {/* Decorative icons - positioned closer to main icon */}
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center transform rotate-12 shadow-sm">
                <Layers className="w-4 h-4 text-yellow-600" />
              </div>
              <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center transform -rotate-12 shadow-sm">
                <Undo className="w-4 h-4 text-green-600" />
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 font-sora">
            {loading ? 'Loading Projects...' : 'Archived Projects'}
          </h2>

          {!loading && (
            <>
              <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                This is where your archived projects live. You can restore them anytime.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
